import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { categoryLabels } from '../utils/api';
import ProductCard from '../components/shop/ProductCard';
import './Shop.css';

const CARE_LEVELS = ['beginner', 'intermediate', 'expert'];
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'rating', label: 'Top Rated' },
  { value: 'popular', label: 'Most Popular' },
];

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [filters, setFilters] = useState({
    keyword: searchParams.get('keyword') || '',
    category: searchParams.get('category') || '',
    minPrice: '',
    maxPrice: '',
    careLevel: '',
    petFriendly: false,
    airPurifying: false,
    featured: searchParams.get('featured') === 'true',
    sort: 'newest',
    page: 1,
  });

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== '' && v !== false)
      );
      const { data } = await api.get('/products', { params });
      setProducts(data.products);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Sync URL keyword changes
  useEffect(() => {
    const kw = searchParams.get('keyword') || '';
    const cat = searchParams.get('category') || '';
    setFilters(f => ({ ...f, keyword: kw, category: cat, page: 1 }));
  }, [searchParams]);

  const updateFilter = (key, value) => {
    setFilters(f => ({ ...f, [key]: value, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      keyword: '', category: '', minPrice: '', maxPrice: '',
      careLevel: '', petFriendly: false, airPurifying: false,
      featured: false, sort: 'newest', page: 1,
    });
    setSearchParams({});
  };

  return (
    <div className="shop-page">
      <div className="shop-header">
        <div className="container">
          <h1 className="section-title">Shop</h1>
          <p className="shop-subtitle">{total} products found</p>
        </div>
      </div>

      <div className="container shop-layout">
        {/* Sidebar Filters */}
        <aside className={`shop-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <h3>Filters</h3>
            <button className="btn btn-ghost btn-sm" onClick={clearFilters}>Clear All</button>
          </div>

          {/* Category */}
          <div className="filter-group">
            <h4 className="filter-title">Category</h4>
            {Object.entries(categoryLabels).map(([key, label]) => (
              <label key={key} className="filter-radio">
                <input
                  type="radio"
                  name="category"
                  checked={filters.category === key}
                  onChange={() => updateFilter('category', filters.category === key ? '' : key)}
                />
                {label}
              </label>
            ))}
          </div>

          {/* Price Range */}
          <div className="filter-group">
            <h4 className="filter-title">Price Range (₹)</h4>
            <div className="price-inputs">
              <input
                type="number"
                placeholder="Min"
                value={filters.minPrice}
                onChange={e => updateFilter('minPrice', e.target.value)}
                className="form-input"
              />
              <span>–</span>
              <input
                type="number"
                placeholder="Max"
                value={filters.maxPrice}
                onChange={e => updateFilter('maxPrice', e.target.value)}
                className="form-input"
              />
            </div>
          </div>

          {/* Care Level */}
          <div className="filter-group">
            <h4 className="filter-title">Care Level</h4>
            {CARE_LEVELS.map(level => (
              <label key={level} className="filter-radio">
                <input
                  type="radio"
                  name="careLevel"
                  checked={filters.careLevel === level}
                  onChange={() => updateFilter('careLevel', filters.careLevel === level ? '' : level)}
                />
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </label>
            ))}
          </div>

          {/* Checkboxes */}
          <div className="filter-group">
            <h4 className="filter-title">Features</h4>
            <label className="filter-check">
              <input
                type="checkbox"
                checked={filters.petFriendly}
                onChange={e => updateFilter('petFriendly', e.target.checked)}
              />
              🐾 Pet Friendly
            </label>
            <label className="filter-check">
              <input
                type="checkbox"
                checked={filters.airPurifying}
                onChange={e => updateFilter('airPurifying', e.target.checked)}
              />
              💨 Air Purifying
            </label>
            <label className="filter-check">
              <input
                type="checkbox"
                checked={filters.featured}
                onChange={e => updateFilter('featured', e.target.checked)}
              />
              ★ Featured Only
            </label>
          </div>
        </aside>

        {/* Main content */}
        <main className="shop-main">
          <div className="shop-toolbar">
            <button className="btn btn-ghost btn-sm filter-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
              ☰ Filters
            </button>
            <div className="sort-select-wrap">
              <select
                value={filters.sort}
                onChange={e => updateFilter('sort', e.target.value)}
                className="sort-select"
              >
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="loading-center"><div className="spinner"></div></div>
          ) : products.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🌱</div>
              <h3>No products found</h3>
              <p>Try adjusting your filters</p>
              <button className="btn btn-primary" onClick={clearFilters} style={{ marginTop: 16 }}>Clear Filters</button>
            </div>
          ) : (
            <>
              <div className="product-grid">
                {products.map(p => <ProductCard key={p._id} product={p} />)}
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div className="pagination">
                  {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      className={`page-btn ${filters.page === p ? 'active' : ''}`}
                      onClick={() => setFilters(f => ({ ...f, page: p }))}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
